import {
  detectMediaType,
  type MediaCategory,
} from '@/components/ui/media-preview-modal/utils';
import { tryCatch } from '@/utils/try-catch';

interface DownloadResult {
  success: boolean;
  error?: string;
}

const CACHE_RELOAD = new Set<MediaCategory>(['image', 'video']);

/** Saves a blob as a file; the URL is revoked a tick later, not mid-download. */
export function downloadBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.download = fileName;
    anchorElement.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export async function downloadFile(
  link: RequestInfo,
  fileName: string,
  options?: RequestInit
): Promise<DownloadResult> {
  if (typeof window === 'undefined') {
    return {
      success: false,
      error: 'Download is only supported in browser context',
    };
  }

  const { category } = detectMediaType(link.toString());

  const { error: fetchError, data: response } = await tryCatch(
    fetch(link, {
      ...(CACHE_RELOAD.has(category) && { cache: 'reload' }),
      ...options,
    })
  );
  if (fetchError) {
    return {
      success: false,
      error: fetchError.message ?? 'Failed to download content',
    };
  }
  if (!response.ok) {
    return {
      success: false,
      error: `HTTP Error: ${response.status} ${response.statusText}`,
    };
  }

  const { error: blobError, data: blob } = await tryCatch(response.blob());
  if (blobError) {
    console.error('Failed to process file data:', blobError);
    return {
      success: false,
      error: blobError.message ?? 'Failed to process file data',
    };
  }

  try {
    downloadBlob(fileName, blob);
    return { success: true };
  } catch (e) {
    console.error('Download initiation failed:', e);
    return {
      success: false,
      error: e instanceof Error ? e.message : 'Failed to initiate download',
    };
  }
}
